import React from 'react';
import { Space, Typography, Button, Drawer, message, Row, Col, Timeline } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';

const { Text, Paragraph } = Typography;

export interface ExecutionDetail {
  run_id: string;
  status: 'success' | 'error';
  started_at: string;
  finished_at: string;
  duration_ms: number;
  agent_id: string;
  input: string;
  node_outputs: Record<string, unknown> | null;
  error: string | null;
  tenant_id: string | null;
}

interface Props {
  detail: ExecutionDetail | null;
  open: boolean;
  onClose: () => void;
}

const ExecutionDetailDrawer: React.FC<Props> = ({ detail, open, onClose }) => (
  <Drawer
    title="Detalles de Ejecución"
    placement="right"
    onClose={onClose}
    open={open}
    width={600}
  >
    {detail && (
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div style={{
          padding: 16,
          background: detail.status === 'success' ? '#f6ffed' : '#fff1f0',
          borderRadius: 8,
          borderLeft: `4px solid ${detail.status === 'success' ? '#52c41a' : '#ff4d4f'}`,
        }}>
          <Row gutter={12} align="middle">
            <Col>
              {detail.status === 'success'
                ? <CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                : <CloseCircleOutlined style={{ fontSize: 24, color: '#ff4d4f' }} />}
            </Col>
            <Col flex="auto">
              <Text strong style={{ fontSize: 16 }}>
                {detail.status === 'success' ? 'Ejecución exitosa' : 'Ejecución fallida'}
              </Text>
              <div><Text type="secondary">{detail.duration_ms / 1000}s</Text></div>
            </Col>
          </Row>
        </div>

        <div>
          <Text strong>Run ID</Text>
          <pre
            style={{ fontSize: 12, wordBreak: 'break-all', background: '#f5f5f5', padding: 8, borderRadius: 4, overflow: 'auto', cursor: 'pointer', marginTop: 8 }}
            onClick={() => { navigator.clipboard.writeText(detail.run_id); message.success('Copiado'); }}
            title="Click para copiar"
          >
            {detail.run_id}
          </pre>
        </div>

        <div>
          <Text strong>Timeline</Text>
          <Timeline style={{ marginTop: 12 }} items={[
            { dot: <CalendarOutlined />, children: <div><Text type="secondary">Inicio</Text><div>{new Date(detail.started_at).toLocaleString('es-ES')}</div></div> },
            { dot: <ClockCircleOutlined />, children: <div><Text type="secondary">Fin</Text><div>{new Date(detail.finished_at).toLocaleString('es-ES')}</div></div> },
            {
              dot: detail.status === 'success' ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
              children: <div><Text type="secondary">Estado</Text><div>{detail.status === 'success' ? 'Completada' : 'Con error'}</div></div>,
            },
          ]} />
        </div>

        <div>
          <Text strong>Input</Text>
          <pre
            style={{ marginTop: 8, padding: 8, background: '#f5f5f5', borderRadius: 4, maxHeight: 200, overflow: 'auto', fontSize: 12, wordBreak: 'break-all', cursor: 'pointer' }}
            onClick={() => { navigator.clipboard.writeText(detail.input); message.success('Copiado'); }}
            title="Click para copiar"
          >
            {detail.input}
          </pre>
        </div>

        {detail.node_outputs && (
          <div>
            <Space align="center" style={{ marginBottom: 8 }}>
              <Text strong>Salidas de Nodos</Text>
              <Button size="small" icon={<CopyOutlined />} type="text"
                onClick={() => { navigator.clipboard.writeText(JSON.stringify(detail.node_outputs, null, 2)); message.success('Copiado'); }}>
                Copiar
              </Button>
            </Space>
            <pre style={{ padding: 8, background: '#f5f5f5', borderRadius: 4, maxHeight: 300, overflow: 'auto', fontSize: 11, wordBreak: 'break-all' }}>
              {JSON.stringify(detail.node_outputs, null, 2)}
            </pre>
          </div>
        )}

        {detail.error && (
          <div style={{ padding: 12, background: '#fff1f0', border: '1px solid #ffccc7', borderRadius: 4 }}>
            <Text strong style={{ color: '#ff4d4f' }}>Error</Text>
            <Paragraph style={{ marginTop: 8, marginBottom: 0, color: '#ff4d4f', fontSize: 12 }}>
              {detail.error}
            </Paragraph>
          </div>
        )}
      </Space>
    )}
  </Drawer>
);

export default ExecutionDetailDrawer;
